import { RedoOutlined, SearchOutlined } from "@ant-design/icons"
import { Button, Card, ConfigProvider, Form, Input, Select, Space, Spin } from "antd"
import type React from "react"
import { memo, useEffect, useState, useMemo } from "react"
import type { VocabularySearchParams } from "@/services/dictionary/vocabularyApi"
import { useQuery } from "@tanstack/react-query"
import { userService } from "@/services/user/userApi"
import { useDebounce } from "ahooks"

interface VocabularySearchFormProps {
  onSearch: (values: Omit<VocabularySearchParams, "page" | "per_page">) => void
  loading?: boolean
  initialValues?: Omit<VocabularySearchParams, "page" | "per_page">
}

const VocabularySearchForm: React.FC<VocabularySearchFormProps> = memo(
  ({ onSearch, loading = false, initialValues }) => {
    const [form] = Form.useForm()
    const [userSearch, setUserSearch] = useState("")
    const debouncedUserSearch = useDebounce(userSearch, { wait: 300 })

    const { data: usersResult, isFetching: loadingUsers } = useQuery({
      queryKey: ["users_search", debouncedUserSearch],
      queryFn: () => userService.list({ page: 1, limit: 50, search: debouncedUserSearch || undefined }),
    })

    const userOptions = useMemo(() => {
      const options =
        usersResult?.data?.map((user: any) => ({
          label: `${user.username || user.name} (${user.email})`,
          value: user.id,
        })) || []

      const initialUserId = initialValues?.["user_id"]
      if (initialUserId && !options.some((o: any) => o.value === initialUserId)) {
        return [{ label: `User ID: ${initialUserId}`, value: initialUserId }, ...options]
      }
      return options
    }, [usersResult, initialValues])

    useEffect(() => {
      if (initialValues) {
        form.setFieldsValue(initialValues)
      }
    }, [initialValues, form])

    const handleSearch = () => {
      form.validateFields().then((values) => {
        onSearch(values)
      })
    }

    const handleReset = () => {
      form.resetFields()
      setUserSearch("")
      onSearch({
        search: "",
        reference_type: undefined,
        user_id: undefined,
      })
    }

    const referenceTypeOptions = [
      { label: "Từ đơn (word)", value: "word" },
      { label: "Cụm động từ (phrasal_verb)", value: "phrasal_verb" },
      { label: "Collocation", value: "collocation" },
      { label: "Thành ngữ (idiom)", value: "idiom" },
      { label: "Cụm từ (phrase)", value: "phrase" },
    ]

    return (
      <ConfigProvider
        theme={{
          components: {
            Form: {
              itemMarginBottom: 0,
            },
          },
        }}
      >
        <Card>
          <Form
            form={form}
            initialValues={{
              search: "",
              reference_type: undefined,
              user_id: undefined,
            }}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5"
          >
            <Form.Item name="search" label="Tìm kiếm" colon={false} className="mb-0">
              <Input allowClear autoComplete="off" placeholder="Nhập từ vựng..." onPressEnter={handleSearch} />
            </Form.Item>

            <Form.Item name="user_id" label="Người lưu" colon={false} className="mb-0">
              <Select
                allowClear
                showSearch
                placeholder="Chọn người lưu"
                filterOption={false}
                onSearch={setUserSearch}
                loading={loadingUsers}
                notFoundContent={loadingUsers ? <Spin size="small" /> : null}
                options={userOptions}
                onChange={handleSearch}
              />
            </Form.Item>

            <Form.Item name="reference_type" label="Loại tham chiếu" colon={false} className="mb-0">
              <Select
                allowClear
                placeholder="Chọn loại từ điển"
                options={referenceTypeOptions}
                onChange={handleSearch}
              />
            </Form.Item>

            <Form.Item label=" " colon={false} className="mb-0">
              <Space>
                <Button type="default" icon={<RedoOutlined />} onClick={handleReset}>
                  Đặt lại
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                >
                  Tìm kiếm
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </ConfigProvider>
    )
  }
)

VocabularySearchForm.displayName = "VocabularySearchForm"

export default VocabularySearchForm
